import React, { useEffect, useState } from 'react';
import { XIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, RefreshCwIcon, UserIcon, BuildingIcon, MailIcon, CalendarIcon, UsersIcon, ClockIcon, MapPinIcon, FileTextIcon, CheckIcon, BarChart3Icon, BrainIcon, AlertCircleIcon, LinkedinIcon, ExternalLinkIcon, NewspaperIcon, DollarSignIcon, HandshakeIcon, MessageSquareIcon, HistoryIcon, ArrowRightIcon, ThumbsUpIcon, ThumbsDownIcon, ShieldIcon, TicketIcon, HeartPulseIcon, HelpCircleIcon, ClipboardListIcon, TrendingUpIcon, AlertTriangleIcon, RocketIcon, SearchIcon, BriefcaseIcon, CpuIcon, CoinsIcon, LineChartIcon, LayoutIcon, Settings2Icon, PackageIcon, TrendingDownIcon, HeadphonesIcon, LoaderIcon } from 'lucide-react';
import { Button } from './Button';
interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function WaitlistModal({
  isOpen,
  onClose
}: WaitlistModalProps) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [persona, setPersona] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [brief, setBrief] = useState<any>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [jobTitle, setJobTitle] = useState('');
  const [isMatching, setIsMatching] = useState(false);
  const [matchResult, setMatchResult] = useState<string | null>(null);
  // Close modal when clicking outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setEmail('');
        setPersona('');
        setIsProcessing(false);
        setBrief(null);
        setExpandedSection(null);
        setJobTitle('');
        setIsMatching(false);
        setMatchResult(null);
      }, 300);
    }
  }, [isOpen]);
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setStep(2);
    }
  };
  const handlePersonaSelect = (selectedPersona: string) => {
    setPersona(selectedPersona);
    setStep(3);
  };
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  const handleAIMatch = () => {
    if (!jobTitle.trim()) return;
    setIsMatching(true);
    setMatchResult(null);
    // Simulate AI matching with timeout
    setTimeout(() => {
      // Simple matching logic - in a real app this would be more sophisticated
      const title = jobTitle.toLowerCase();
      let match = '';
      if (title.includes('sales') || title.includes('account') || title.includes('business development')) {
        match = 'sales';
      } else if (title.includes('success') || title.includes('customer') || title.includes('client')) {
        match = 'customer';
      } else if (title.includes('consult')) {
        match = 'consultant';
      } else if (title.includes('ceo') || title.includes('cto') || title.includes('cfo') || title.includes('executive') || title.includes('director')) {
        match = 'executive';
      } else if (title.includes('project') || title.includes('program') || title.includes('manager')) {
        match = 'project';
      } else if (title.includes('operations') || title.includes('ops')) {
        match = 'operations';
      } else if (title.includes('product')) {
        match = 'product';
      } else if (title.includes('market')) {
        match = 'marketing';
      } else if (title.includes('engineer') || title.includes('developer') || title.includes('technical') || title.includes('tech')) {
        match = 'technical';
      } else if (title.includes('support') || title.includes('help') || title.includes('service')) {
        match = 'support';
      } else {
        // Default to a random persona if no match
        const personas = ['sales', 'customer', 'consultant', 'executive', 'project', 'operations', 'product', 'marketing', 'technical', 'support'];
        match = personas[Math.floor(Math.random() * personas.length)];
      }
      setMatchResult(match);
      setIsMatching(false);
    }, 1500);
  };
  const handleGenerateBrief = () => {
    setIsProcessing(true);
    setStep(4);
    // Simulate API call to generate brief
    setTimeout(() => {
      // Generate different brief based on selected persona
      const briefData = {
        meeting: {
          title: 'Quarterly Strategy Meeting',
          time: 'Tomorrow, 10:00 AM - 11:30 AM',
          location: 'Google Meet',
          agenda: 'Review Q3 results, plan Q4 initiatives, discuss resource allocation'
        },
        companyInsights: {
          name: 'TechCorp, Inc.',
          summary: "TechCorp is a growing player in the enterprise software space, specializing in data integration solutions. They've been expanding their market presence in the APAC region over the past year.",
          linkedInUrl: 'linkedin.com/company/techcorp-inc',
          recentNews: [{
            type: 'funding',
            content: 'Raised Series C ($45M) led by Accel Partners',
            date: '3 months ago'
          }, {
            type: 'product',
            content: 'Launched TechCorp Connect, a new API integration platform',
            date: '1 month ago'
          }, {
            type: 'partnership',
            content: 'Announced strategic partnership with Microsoft for cloud solutions',
            date: '2 weeks ago'
          }],
          metrics: {
            growth: '+27% YoY',
            employees: '250-500',
            funding: '$72M total raised',
            valuation: '$320M (estimated)'
          },
          disclaimer: 'Note: Some information is simulated for demonstration purposes.'
        },
        attendees: [{
          name: 'William Smith',
          role: 'VP of Product',
          company: 'TechCorp, Inc.',
          linkedInUrl: 'linkedin.com/in/williamsmith',
          background: 'Previously Director of Product at InnovateCo for 5 years. MBA from Stanford. Specializes in SaaS product strategy and go-to-market planning.',
          verified: true
        }, {
          name: 'Alice Johnson',
          role: 'Director of Marketing',
          company: 'TechCorp, Inc.',
          linkedInUrl: 'linkedin.com/in/alicejohnson',
          background: '15+ years in B2B marketing. Led the recent rebranding initiative at TechCorp. Strong focus on content marketing and demand generation.',
          verified: true
        }, {
          name: 'Robert Chen',
          role: 'Engineering Manager',
          company: 'TechCorp, Inc.',
          linkedInUrl: 'linkedin.com/in/robertchen',
          background: 'Leads the API development team. Previously at Amazon Web Services. Computer Science degree from MIT.',
          verified: false
        }],
        personaSection: {
          sales: {
            potentialObjections: ['Current integration process works well enough, why change?', 'Price point seems high compared to competitors', 'Implementation timeline is too long for our current needs', 'Security concerns about third-party data access'],
            objectionCounters: ['ROI analysis shows 40% reduction in integration time and 25% cost savings', 'Premium features justify the price difference - total cost of ownership is lower', 'Phased implementation approach available with quick wins in first 30 days', 'SOC 2 Type II certified, with end-to-end encryption and dedicated security team'],
            discoveryQuestions: ['What are your biggest pain points with your current integration process?', 'How are you measuring success for your integration initiatives?', 'What would an ideal timeline look like for implementation?', 'Who else is involved in the decision-making process?']
          },
          customer: {
            customerHealth: 'At Risk',
            accountStatus: 'Enterprise Plan ($75K ARR)',
            renewalDate: 'March 15, 2024 (4 months away)',
            openCases: [{
              id: 'CASE-4291',
              priority: 'High',
              title: 'API connection failures during peak hours',
              status: 'In Progress',
              assignee: 'Support Team',
              lastUpdated: '2 days ago'
            }, {
              id: 'CASE-4315',
              priority: 'Medium',
              title: 'Dashboard loading slowly for large datasets',
              status: 'Under Investigation',
              assignee: 'Engineering',
              lastUpdated: '5 days ago'
            }],
            csNotes: ['Customer has expressed frustration with recent performance issues', 'Training session for new team members scheduled next week', 'Executive sponsor (CTO) needs to see improvement before renewal'],
            usageMetrics: {
              activeUsers: '42/50 seats used',
              apiCalls: 'Down 15% month-over-month',
              featureAdoption: '67% of available features'
            }
          },
          consultant: {
            projectScope: 'Data Integration Platform Modernization',
            discoveryFraming: ['Current state assessment of integration architecture', 'Pain point identification and prioritization', 'Future state vision and roadmap development'],
            risks: ['Legacy system dependencies may complicate migration', 'Data governance policies need updating', 'Internal resource constraints for implementation support'],
            deliverables: ['Current state assessment report (Week 2)', 'Future state architecture blueprint (Week 4)', 'Implementation roadmap with phased approach (Week 6)', 'ROI analysis and business case (Week 8)'],
            pilotPlan: {
              timeline: '8-week pilot program',
              scope: 'Marketing data integration workflow',
              success: '50% reduction in manual data processing time'
            },
            kpis: ['Integration development time reduction', 'Data quality improvement metrics', 'Operational cost savings', 'Time-to-insight for business users']
          }
        },
        previousEngagement: [{
          date: 'October 10, 2023',
          summary: 'Initial discovery call discussing integration challenges and potential solutions.'
        }, {
          date: 'October 17, 2023',
          summary: 'Product demo with technical team. Positive feedback on API capabilities.'
        }, {
          date: 'October 25, 2023',
          summary: 'Pricing proposal sent. William requested additional information on implementation timeline.'
        }, {
          date: 'November 2, 2023',
          summary: 'Follow-up call with Alice to address marketing use cases and integration with their CRM.'
        }],
        transcriptInsights: {
          commitments: [{
            status: 'outstanding',
            description: 'Send security whitepaper and compliance documentation',
            owner: 'Our team',
            priority: 'high'
          }, {
            status: 'pending',
            description: 'Schedule technical deep dive with engineering team',
            owner: 'Both parties',
            priority: 'medium'
          }, {
            status: 'resolved',
            description: 'Provide custom pricing proposal for enterprise tier',
            owner: 'Our team',
            priority: 'medium'
          }],
          sentimentHighlights: ['William (VP Product) is focused on technical capabilities and implementation timeline', 'Alice (Marketing) is concerned about ease of use for non-technical team members', 'Executive sponsor mentioned ROI expectations of 3x within first year']
        }
      };
      setBrief(briefData);
      setIsProcessing(false);
      setStep(5);
    }, 2500);
  };
  if (!isOpen) return null;
  // Function to get icon for each persona
  const getPersonaIcon = (personaId: string) => {
    switch (personaId) {
      case 'sales':
        return <CoinsIcon className="h-5 w-5" data-id="element-805" />;
      case 'customer':
        return <HeartPulseIcon className="h-5 w-5" data-id="element-806" />;
      case 'consultant':
        return <ClipboardListIcon className="h-5 w-5" data-id="element-807" />;
      case 'executive':
        return <BriefcaseIcon className="h-5 w-5" data-id="element-808" />;
      case 'project':
        return <CalendarIcon className="h-5 w-5" data-id="element-809" />;
      case 'operations':
        return <Settings2Icon className="h-5 w-5" data-id="element-810" />;
      case 'product':
        return <PackageIcon className="h-5 w-5" data-id="element-811" />;
      case 'marketing':
        return <TrendingUpIcon className="h-5 w-5" data-id="element-812" />;
      case 'technical':
        return <CpuIcon className="h-5 w-5" data-id="element-813" />;
      case 'support':
        return <HeadphonesIcon className="h-5 w-5" data-id="element-814" />;
      default:
        return <UserIcon className="h-5 w-5" data-id="element-815" />;
    }
  };
  // Function to get display name for each persona
  const getPersonaName = (personaId: string) => {
    switch (personaId) {
      case 'sales':
        return 'Sales';
      case 'customer':
        return 'Customer Success';
      case 'consultant':
        return 'Consultant';
      case 'executive':
        return 'Executive';
      case 'project':
        return 'Project Manager';
      case 'operations':
        return 'Operations';
      case 'product':
        return 'Product';
      case 'marketing':
        return 'Marketing';
      case 'technical':
        return 'Technical';
      case 'support':
        return 'Support';
      default:
        return personaId;
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={handleBackdropClick} data-id="element-816">
      <div className="bg-[#1E2A38] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-id="element-817">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#F5F7FA] border-opacity-10 sticky top-0 bg-[#1E2A38] z-10" data-id="element-818">
          <h3 className="text-xl font-bold text-[#F5F7FA]" data-id="element-819">
            {step < 5 ? 'Join the Waitlist' : 'Meeting Brief Preview'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F5F7FA] hover:bg-opacity-10 transition-colors" data-id="element-820">
            <XIcon className="h-5 w-5 text-[#F5F7FA]" data-id="element-821" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-822">
          {/* Step 1: Email */}
          {step === 1 && <div data-id="element-823">
              <p className="text-[#F5F7FA] text-opacity-80 mb-6" data-id="element-824">
                Enter your email to join our waitlist and be the first to know
                when we launch.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4" data-id="element-825">
                <div data-id="element-826">
                  <label htmlFor="email" className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-827">
                    Email address
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" placeholder="you@example.com" required data-id="element-828" />
                </div>
                <Button type="submit" className="w-full" data-id="element-829">
                  Continue
                </Button>
              </form>
            </div>}
          {/* Step 2: Persona Selection */}
          {step === 2 && <div data-id="element-830">
              <p className="text-[#F5F7FA] text-opacity-80 mb-6" data-id="element-831">
                Select your role to generate a tailored meeting brief
              </p>
              {/* AI Matching Section */}
              <div className="mb-6 p-4 bg-[#141E2A] rounded-lg border border-[#F5F7FA] border-opacity-10" data-id="element-832">
                <p className="text-sm text-[#F5F7FA] mb-3" data-id="element-833">
                  Tell us your job title and let AI match you to a persona
                </p>
                <div className="flex gap-2 mb-3" data-id="element-834">
                  <input type="text" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Sales Development Representative" className="flex-1 bg-[#1A2331] border border-[#F5F7FA] border-opacity-10 rounded-lg px-3 py-2 text-[#F5F7FA] text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" data-id="element-835" />
                  <button onClick={handleAIMatch} disabled={isMatching || !jobTitle.trim()} className="bg-[#1A2331] hover:bg-[#232D3F] disabled:opacity-50 border border-[#F5F7FA] border-opacity-10 rounded-lg px-3 py-2 text-[#F5F7FA] flex items-center transition-colors" data-id="element-836">
                    {isMatching ? <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-837" /> : <SearchIcon className="h-4 w-4" data-id="element-838" />}
                  </button>
                </div>
                {/* Match Result */}
                {matchResult && <div className="bg-[#C8F169] bg-opacity-10 border border-[#C8F169] border-opacity-20 rounded-lg p-3 flex items-center" data-id="element-839">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-2 flex-shrink-0" data-id="element-840" />
                    <div data-id="element-841">
                      <p className="text-sm font-medium text-[#F5F7FA]" data-id="element-842">
                        Best match:{' '}
                        <span className="text-[#C8F169]" data-id="element-843">
                          {getPersonaName(matchResult)}
                        </span>
                      </p>
                      <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-844">
                        Click on the persona below to select it
                      </p>
                    </div>
                  </div>}
              </div>
              {/* Persona List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-845">
                {['sales', 'customer', 'consultant', 'executive', 'project', 'operations', 'product', 'marketing', 'technical', 'support'].map(p => <div key={p} onClick={() => handlePersonaSelect(p)} className={`flex items-center p-3 border border-[#F5F7FA] border-opacity-10 rounded-lg hover:border-[#C8F169] hover:bg-[#C8F169] hover:bg-opacity-5 cursor-pointer transition-colors ${matchResult === p ? 'border-[#C8F169] bg-[#C8F169] bg-opacity-5' : ''}`} data-id="element-846">
                    <div className="bg-[#F5F7FA] bg-opacity-10 p-2 rounded-lg mr-3" data-id="element-847">
                      {getPersonaIcon(p)}
                    </div>
                    <span className="text-[#F5F7FA]" data-id="element-848">{getPersonaName(p)}</span>
                  </div>)}
              </div>
            </div>}
          {/* Step 3: Example Meeting */}
          {step === 3 && <div data-id="element-849">
              <p className="text-[#F5F7FA] text-opacity-80 mb-6" data-id="element-850">
                This is an example of an upcoming meeting
              </p>
              <div className="bg-[#141E2A] p-4 rounded-lg border border-[#F5F7FA] border-opacity-10 mb-6" data-id="element-851">
                <div className="flex items-start mb-4" data-id="element-852">
                  <BuildingIcon className="h-5 w-5 text-[#C8F169] mr-2 mt-1 flex-shrink-0" data-id="element-853" />
                  <div data-id="element-854">
                    <label className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-855">
                      Company Name
                    </label>
                    <p className="text-[#F5F7FA]" data-id="element-856">TechCorp, Inc.</p>
                  </div>
                </div>
                <div className="flex items-start mb-4" data-id="element-857">
                  <CalendarIcon className="h-5 w-5 text-[#C8F169] mr-2 mt-1 flex-shrink-0" data-id="element-858" />
                  <div data-id="element-859">
                    <label className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-860">
                      Meeting
                    </label>
                    <p className="text-[#F5F7FA]" data-id="element-861">Quarterly Strategy Meeting</p>
                    <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-862">
                      Tomorrow, 10:00 AM - 11:30 AM
                    </p>
                  </div>
                </div>
                <div className="flex items-start mb-4" data-id="element-863">
                  <UsersIcon className="h-5 w-5 text-[#C8F169] mr-2 mt-1 flex-shrink-0" data-id="element-864" />
                  <div data-id="element-865">
                    <label className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-866">
                      Attendees
                    </label>
                    <p className="text-[#F5F7FA]" data-id="element-867">
                      William Smith, Alice Johnson, Robert Chen
                    </p>
                  </div>
                </div>
              </div>
              <Button onClick={handleGenerateBrief} className="w-full" data-id="element-868">
                Generate Brief
              </Button>
            </div>}
          {/* Step 4: Processing */}
          {step === 4 && <div className="flex flex-col items-center justify-center py-12" data-id="element-869">
              <div className="mb-6 relative" data-id="element-870">
                <div className="w-16 h-16 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" data-id="element-871"></div>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-872">
                  <FileTextIcon className="h-6 w-6 text-[#3B82F6]" data-id="element-873" />
                </div>
              </div>
              <h3 className="text-xl font-medium mb-2 text-[#F5F7FA]" data-id="element-874">
                Generating Your Brief
              </h3>
              <p className="text-[#F5F7FA] text-opacity-70 text-center max-w-md" data-id="element-875">
                We're analyzing the meeting and preparing a comprehensive brief
                with company insights, attendee profiles, and relevant context
                based on your role.
              </p>
            </div>}
          {/* Step 5: Brief Display */}
          {step === 5 && brief && <div className="space-y-6" data-id="element-876">
              {/* Meeting Header */}
              <div className="bg-[#141E2A] p-4 rounded-lg border border-[#F5F7FA] border-opacity-10" data-id="element-877">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-878">
                  <div data-id="element-879">
                    <h4 className="font-medium text-lg text-[#F5F7FA]" data-id="element-880">
                      {brief.meeting.title}
                    </h4>
                    <div className="flex flex-wrap gap-3 mt-1" data-id="element-881">
                      <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-882">
                        <ClockIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-883" />
                        <span data-id="element-884">{brief.meeting.time}</span>
                      </div>
                      <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-885">
                        <MapPinIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-886" />
                        <span data-id="element-887">{brief.meeting.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs bg-[#C8F169] bg-opacity-20 text-[#C8F169] px-2 py-1 rounded inline-flex items-center" data-id="element-888">
                    <FileTextIcon className="h-3 w-3 mr-1" data-id="element-889" />
                    AI-Generated Brief
                  </div>
                </div>
              </div>
              {/* Section 1: Company Insights */}
              <div className="border border-[#F5F7FA] border-opacity-10 rounded-lg overflow-hidden" data-id="element-890">
                <div className="bg-[#1A2331] p-3 flex items-center justify-between cursor-pointer" onClick={() => toggleSection('company')} data-id="element-891">
                  <div className="flex items-center" data-id="element-892">
                    <BuildingIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-893" />
                    <h5 className="font-medium text-[#F5F7FA]" data-id="element-894">
                      Company Insights
                    </h5>
                  </div>
                  {expandedSection === 'company' ? <ChevronUpIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-895" /> : <ChevronDownIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-896" />}
                </div>
                {expandedSection === 'company' && <div className="p-4 bg-[#141E2A]" data-id="element-897">
                    <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-898">
                      <div className="flex-1" data-id="element-899">
                        <div className="flex items-center justify-between mb-2" data-id="element-900">
                          <h6 className="font-medium text-[#F5F7FA]" data-id="element-901">
                            {brief.companyInsights.name}
                          </h6>
                          <a href="#" className="text-[#C8F169] text-xs flex items-center" data-id="element-902">
                            <LinkedinIcon className="h-3 w-3 mr-1" data-id="element-903" />
                            LinkedIn
                          </a>
                        </div>
                        <p className="text-sm text-[#F5F7FA] text-opacity-90 mb-4" data-id="element-904">
                          {brief.companyInsights.summary}
                        </p>
                        <h6 className="text-sm font-medium mb-2 flex items-center" data-id="element-905">
                          <NewspaperIcon className="h-3.5 w-3.5 mr-1.5 text-[#C8F169]" data-id="element-906" />
                          Recent News & Events
                        </h6>
                        <ul className="space-y-2 mb-4" data-id="element-907">
                          {brief.companyInsights.recentNews.map((news, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-908">
                              {news.type === 'funding' && <DollarSignIcon className="h-3.5 w-3.5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-909" />}
                              {news.type === 'product' && <RocketIcon className="h-3.5 w-3.5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-910" />}
                              {news.type === 'partnership' && <HandshakeIcon className="h-3.5 w-3.5 text-purple-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-911" />}
                              {news.type === 'regulatory' && <div className="h-3.5 w-3.5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-912" />}
                              <div data-id="element-913">
                                <span className="text-[#F5F7FA]" data-id="element-914">
                                  {news.content}
                                </span>
                                <span className="text-[#F5F7FA] text-opacity-60 ml-1" data-id="element-915">
                                  ({news.date})
                                </span>
                              </div>
                            </li>)}
                        </ul>
                      </div>
                      <div className="md:w-1/3 bg-[#1A2331] p-3 rounded-lg" data-id="element-916">
                        <h6 className="text-sm font-medium mb-3" data-id="element-917">
                          Company Metrics
                        </h6>
                        <div className="space-y-3" data-id="element-918">
                          <div data-id="element-919">
                            <div className="flex justify-between text-sm mb-1" data-id="element-920">
                              <span className="text-[#F5F7FA] text-opacity-70" data-id="element-921">
                                Growth
                              </span>
                              <span className="text-green-500" data-id="element-922">
                                {brief.companyInsights.metrics.growth}
                              </span>
                            </div>
                          </div>
                          <div data-id="element-923">
                            <div className="flex justify-between text-sm mb-1" data-id="element-924">
                              <span className="text-[#F5F7FA] text-opacity-70" data-id="element-925">
                                Employees
                              </span>
                              <span data-id="element-926">
                                {brief.companyInsights.metrics.employees}
                              </span>
                            </div>
                          </div>
                          <div data-id="element-927">
                            <div className="flex justify-between text-sm mb-1" data-id="element-928">
                              <span className="text-[#F5F7FA] text-opacity-70" data-id="element-929">
                                Total Funding
                              </span>
                              <span data-id="element-930">
                                {brief.companyInsights.metrics.funding}
                              </span>
                            </div>
                          </div>
                          <div data-id="element-931">
                            <div className="flex justify-between text-sm mb-1" data-id="element-932">
                              <span className="text-[#F5F7FA] text-opacity-70" data-id="element-933">
                                Valuation
                              </span>
                              <span data-id="element-934">
                                {brief.companyInsights.metrics.valuation}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#F5F7FA] border-opacity-10 text-xs text-[#F5F7FA] text-opacity-50 italic" data-id="element-935">
                      {brief.companyInsights.disclaimer}
                    </div>
                  </div>}
              </div>
              {/* Section 2: Attendee Breakdown */}
              <div className="border border-[#F5F7FA] border-opacity-10 rounded-lg overflow-hidden" data-id="element-936">
                <div className="bg-[#1A2331] p-3 flex items-center justify-between cursor-pointer" onClick={() => toggleSection('attendees')} data-id="element-937">
                  <div className="flex items-center" data-id="element-938">
                    <UsersIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-939" />
                    <h5 className="font-medium text-[#F5F7FA]" data-id="element-940">
                      Attendee Breakdown
                    </h5>
                  </div>
                  {expandedSection === 'attendees' ? <ChevronUpIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-941" /> : <ChevronDownIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-942" />}
                </div>
                {expandedSection === 'attendees' && <div className="p-4 bg-[#141E2A]" data-id="element-943">
                    <div className="space-y-4" data-id="element-944">
                      {brief.attendees.map((attendee, idx) => <div key={idx} className="bg-[#1A2331] p-4 rounded-lg" data-id="element-945">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3" data-id="element-946">
                            <div className="flex items-center" data-id="element-947">
                              <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-sm font-bold mr-3" data-id="element-948">
                                {attendee.name.split(' ').map((n: string) => n[0]).join('')}
                              </div>
                              <div data-id="element-949">
                                <div className="font-medium text-[#F5F7FA] flex items-center" data-id="element-950">
                                  {attendee.name}
                                  {attendee.verified ? <CheckCircleIcon className="h-3.5 w-3.5 text-[#C8F169] ml-1" data-id="element-951" /> : null}
                                </div>
                                <div className="text-sm text-[#F5F7FA] text-opacity-70 flex items-center" data-id="element-952">
                                  {attendee.role}
                                  {!attendee.verified && <span className="text-xs ml-1 text-[#F5F7FA] text-opacity-50" data-id="element-953">
                                      (role unconfirmed)
                                    </span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-954">
                              <span className="text-xs bg-[#F5F7FA] bg-opacity-10 px-2 py-0.5 rounded" data-id="element-955">
                                {attendee.company}
                              </span>
                              <a href="#" className="text-[#C8F169] text-xs flex items-center" data-id="element-956">
                                <LinkedinIcon className="h-3 w-3 mr-1" data-id="element-957" />
                                LinkedIn
                              </a>
                            </div>
                          </div>
                          <div className="text-sm text-[#F5F7FA] text-opacity-90 bg-[#141E2A] p-3 rounded" data-id="element-958">
                            <h6 className="text-xs font-medium mb-1 text-[#F5F7FA] text-opacity-70" data-id="element-959">
                              Background
                            </h6>
                            <p data-id="element-960">{attendee.background}</p>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </div>
              {/* Section 3: Persona-Specific Variable Section */}
              <div className="border border-[#F5F7FA] border-opacity-10 rounded-lg overflow-hidden" data-id="element-961">
                <div className="bg-[#1A2331] p-3 flex items-center justify-between cursor-pointer" onClick={() => toggleSection('persona')} data-id="element-962">
                  <div className="flex items-center" data-id="element-963">
                    {persona === 'sales' && <>
                        <BarChart3Icon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-964" />
                        <h5 className="font-medium text-[#F5F7FA]" data-id="element-965">
                          Sales Strategy
                        </h5>
                      </>}
                    {persona === 'customer' && <>
                        <HeartPulseIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-966" />
                        <h5 className="font-medium text-[#F5F7FA]" data-id="element-967">
                          Customer Health & Support
                        </h5>
                      </>}
                    {persona === 'consultant' && <>
                        <ClipboardListIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-968" />
                        <h5 className="font-medium text-[#F5F7FA]" data-id="element-969">
                          Consulting Framework
                        </h5>
                      </>}
                  </div>
                  {expandedSection === 'persona' ? <ChevronUpIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-970" /> : <ChevronDownIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-971" />}
                </div>
                {expandedSection === 'persona' && <div className="p-4 bg-[#141E2A]" data-id="element-972">
                    {/* Sales Persona Content */}
                    {persona === 'sales' && <div className="space-y-5" data-id="element-973">
                        <div data-id="element-974">
                          <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-975">
                            <AlertCircleIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-976" />
                            Potential Objections & Counters
                          </h6>
                          <div className="space-y-3" data-id="element-977">
                            {brief.personaSection.sales.potentialObjections.map((objection, idx) => <div key={idx} className="bg-[#1A2331] p-3 rounded-lg" data-id="element-978">
                                  <p className="text-sm text-[#F5F7FA] mb-2 font-medium" data-id="element-979">
                                    <span className="text-amber-500 mr-1" data-id="element-980">
                                      Objection:
                                    </span>{' '}
                                    {objection}
                                  </p>
                                  <p className="text-sm text-[#F5F7FA] text-opacity-90 pl-3 border-l-2 border-[#C8F169]" data-id="element-981">
                                    <span className="text-[#C8F169] mr-1" data-id="element-982">
                                      Counter:
                                    </span>{' '}
                                    {brief.personaSection.sales.objectionCounters[idx]}
                                  </p>
                                </div>)}
                          </div>
                        </div>
                        <div data-id="element-983">
                          <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-984">
                            <HelpCircleIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-985" />
                            Discovery Questions
                          </h6>
                          <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-986">
                            <ul className="space-y-2" data-id="element-987">
                              {brief.personaSection.sales.discoveryQuestions.map((question, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-988">
                                    <span className="text-[#C8F169] mr-2" data-id="element-989">
                                      •
                                    </span>
                                    <span data-id="element-990">{question}</span>
                                  </li>)}
                            </ul>
                          </div>
                        </div>
                      </div>}
                    {/* Customer Success Persona Content */}
                    {persona === 'customer' && <div className="space-y-5" data-id="element-991">
                        <div className="flex flex-col md:flex-row gap-4" data-id="element-992">
                          <div className="md:w-1/3 bg-[#1A2331] p-3 rounded-lg" data-id="element-993">
                            <h6 className="text-sm font-medium mb-3" data-id="element-994">
                              Account Overview
                            </h6>
                            <div className="space-y-3" data-id="element-995">
                              <div data-id="element-996">
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-997">
                                  Customer Health
                                </p>
                                <div className="flex items-center" data-id="element-998">
                                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-999"></div>
                                  <span className="text-red-500 font-medium" data-id="element-1000">
                                    {brief.personaSection.customer.customerHealth}
                                  </span>
                                </div>
                              </div>
                              <div data-id="element-1001">
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1002">
                                  Account Status
                                </p>
                                <p className="text-sm" data-id="element-1003">
                                  {brief.personaSection.customer.accountStatus}
                                </p>
                              </div>
                              <div data-id="element-1004">
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1005">
                                  Renewal Date
                                </p>
                                <p className="text-sm" data-id="element-1006">
                                  {brief.personaSection.customer.renewalDate}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1" data-id="element-1007">
                            <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1008">
                              <TicketIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-1009" />
                              Open Support Cases
                            </h6>
                            <div className="space-y-2" data-id="element-1010">
                              {brief.personaSection.customer.openCases.map((caseItem, idx) => <div key={idx} className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1011">
                                    <div className="flex justify-between items-start mb-1" data-id="element-1012">
                                      <div className="flex items-center" data-id="element-1013">
                                        <span className="text-xs bg-[#F5F7FA] bg-opacity-10 px-1.5 py-0.5 rounded mr-2" data-id="element-1014">
                                          {caseItem.id}
                                        </span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${caseItem.priority === 'High' ? 'bg-red-500 bg-opacity-20 text-red-500' : 'bg-yellow-500 bg-opacity-20 text-yellow-500'}`} data-id="element-1015">
                                          {caseItem.priority}
                                        </span>
                                      </div>
                                      <span className="text-xs text-[#F5F7FA] text-opacity-50" data-id="element-1016">
                                        {caseItem.lastUpdated}
                                      </span>
                                    </div>
                                    <p className="text-sm mb-1" data-id="element-1017">
                                      {caseItem.title}
                                    </p>
                                    <div className="flex justify-between text-xs text-[#F5F7FA] text-opacity-70" data-id="element-1018">
                                      <span data-id="element-1019">Status: {caseItem.status}</span>
                                      <span data-id="element-1020">Assignee: {caseItem.assignee}</span>
                                    </div>
                                  </div>)}
                            </div>
                          </div>
                        </div>
                        <div data-id="element-1021">
                          <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1022">
                            <MessageSquareIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1023" />
                            CS Notes
                          </h6>
                          <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1024">
                            <ul className="space-y-2" data-id="element-1025">
                              {brief.personaSection.customer.csNotes.map((note, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-1026">
                                    <span className="text-[#C8F169] mr-2" data-id="element-1027">
                                      •
                                    </span>
                                    <span data-id="element-1028">{note}</span>
                                  </li>)}
                            </ul>
                          </div>
                        </div>
                        <div data-id="element-1029">
                          <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1030">
                            <TrendingUpIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1031" />
                            Usage Metrics
                          </h6>
                          <div className="grid grid-cols-3 gap-3" data-id="element-1032">
                            <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1033">
                              <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1034">
                                Active Users
                              </p>
                              <p className="text-sm font-medium" data-id="element-1035">
                                {brief.personaSection.customer.usageMetrics.activeUsers}
                              </p>
                            </div>
                            <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1036">
                              <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1037">
                                API Calls
                              </p>
                              <p className="text-sm font-medium text-red-500" data-id="element-1038">
                                {brief.personaSection.customer.usageMetrics.apiCalls}
                              </p>
                            </div>
                            <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1039">
                              <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1040">
                                Feature Adoption
                              </p>
                              <p className="text-sm font-medium" data-id="element-1041">
                                {brief.personaSection.customer.usageMetrics.featureAdoption}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>}
                    {/* Consultant Persona Content */}
                    {persona === 'consultant' && <div className="space-y-5" data-id="element-1042">
                        <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1043">
                          <h6 className="text-sm font-medium mb-2" data-id="element-1044">
                            Project Scope
                          </h6>
                          <p className="text-[#C8F169] font-medium" data-id="element-1045">
                            {brief.personaSection.consultant.projectScope}
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1046">
                          <div data-id="element-1047">
                            <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1048">
                              <ClipboardListIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1049" />
                              Discovery Framing
                            </h6>
                            <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1050">
                              <ul className="space-y-2" data-id="element-1051">
                                {brief.personaSection.consultant.discoveryFraming.map((item, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-1052">
                                      <span className="text-[#C8F169] mr-2" data-id="element-1053">
                                        •
                                      </span>
                                      <span data-id="element-1054">{item}</span>
                                    </li>)}
                              </ul>
                            </div>
                          </div>
                          <div data-id="element-1055">
                            <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1056">
                              <AlertTriangleIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-1057" />
                              Project Risks
                            </h6>
                            <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1058">
                              <ul className="space-y-2" data-id="element-1059">
                                {brief.personaSection.consultant.risks.map((risk, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-1060">
                                      <span className="text-amber-500 mr-2" data-id="element-1061">
                                        •
                                      </span>
                                      <span data-id="element-1062">{risk}</span>
                                    </li>)}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-1063">
                          <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1064">
                            <CheckIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1065" />
                            Key Deliverables
                          </h6>
                          <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1066">
                            <ul className="space-y-2" data-id="element-1067">
                              {brief.personaSection.consultant.deliverables.map((deliverable, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-1068">
                                    <span className="text-[#C8F169] mr-2" data-id="element-1069">
                                      {idx + 1}.
                                    </span>
                                    <span data-id="element-1070">{deliverable}</span>
                                  </li>)}
                            </ul>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1071">
                          <div data-id="element-1072">
                            <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1073">
                              <RocketIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1074" />
                              Pilot Blueprint
                            </h6>
                            <div className="bg-[#1A2331] p-3 rounded-lg space-y-2" data-id="element-1075">
                              <div data-id="element-1076">
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1077">
                                  Timeline
                                </p>
                                <p className="text-sm" data-id="element-1078">
                                  {brief.personaSection.consultant.pilotPlan.timeline}
                                </p>
                              </div>
                              <div data-id="element-1079">
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1080">
                                  Scope
                                </p>
                                <p className="text-sm" data-id="element-1081">
                                  {brief.personaSection.consultant.pilotPlan.scope}
                                </p>
                              </div>
                              <div data-id="element-1082">
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-1083">
                                  Success Criteria
                                </p>
                                <p className="text-sm" data-id="element-1084">
                                  {brief.personaSection.consultant.pilotPlan.success}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div data-id="element-1085">
                            <h6 className="text-sm font-medium mb-3 flex items-center" data-id="element-1086">
                              <BarChart3Icon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1087" />
                              Success KPIs
                            </h6>
                            <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1088">
                              <ul className="space-y-2" data-id="element-1089">
                                {brief.personaSection.consultant.kpis.map((kpi, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-1090">
                                      <span className="text-[#C8F169] mr-2" data-id="element-1091">
                                        •
                                      </span>
                                      <span data-id="element-1092">{kpi}</span>
                                    </li>)}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>}
              </div>
              {/* Section 4: Previous Engagement */}
              <div className="border border-[#F5F7FA] border-opacity-10 rounded-lg overflow-hidden" data-id="element-1093">
                <div className="bg-[#1A2331] p-3 flex items-center justify-between cursor-pointer" onClick={() => toggleSection('engagement')} data-id="element-1094">
                  <div className="flex items-center" data-id="element-1095">
                    <HistoryIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1096" />
                    <h5 className="font-medium text-[#F5F7FA]" data-id="element-1097">
                      Previous Engagement
                    </h5>
                  </div>
                  {expandedSection === 'engagement' ? <ChevronUpIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-1098" /> : <ChevronDownIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-1099" />}
                </div>
                {expandedSection === 'engagement' && <div className="p-4 bg-[#141E2A]" data-id="element-1100">
                    <div className="relative pl-6 border-l border-[#F5F7FA] border-opacity-10" data-id="element-1101">
                      {brief.previousEngagement.map((item, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-1102">
                          <div className="absolute left-0 w-3 h-3 rounded-full bg-[#C8F169] transform -translate-x-1.5" data-id="element-1103"></div>
                          <p className="text-xs text-[#C8F169] mb-1" data-id="element-1104">
                            {item.date}
                          </p>
                          <p className="text-sm text-[#F5F7FA]" data-id="element-1105">
                            {item.summary}
                          </p>
                        </div>)}
                    </div>
                  </div>}
              </div>
              {/* Section 5: Transcript Insights & Commitments */}
              <div className="border border-[#F5F7FA] border-opacity-10 rounded-lg overflow-hidden" data-id="element-1106">
                <div className="bg-[#1A2331] p-3 flex items-center justify-between cursor-pointer" onClick={() => toggleSection('transcript')} data-id="element-1107">
                  <div className="flex items-center" data-id="element-1108">
                    <MessageSquareIcon className="h-4 w-4 text-[#C8F169] mr-2" data-id="element-1109" />
                    <h5 className="font-medium text-[#F5F7FA]" data-id="element-1110">
                      Transcript Insights & Commitments
                    </h5>
                  </div>
                  {expandedSection === 'transcript' ? <ChevronUpIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-1111" /> : <ChevronDownIcon className="h-4 w-4 text-[#F5F7FA] text-opacity-50" data-id="element-1112" />}
                </div>
                {expandedSection === 'transcript' && <div className="p-4 bg-[#141E2A]" data-id="element-1113">
                    <div className="mb-4" data-id="element-1114">
                      <h6 className="text-sm font-medium mb-3" data-id="element-1115">
                        Action Items & Commitments
                      </h6>
                      <div className="space-y-2" data-id="element-1116">
                        {brief.transcriptInsights.commitments.filter(c => c.status === 'outstanding').map((item, idx) => <div key={idx} className="flex items-start bg-red-500 bg-opacity-10 p-3 rounded-lg" data-id="element-1117">
                              <AlertCircleIcon className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-1118" />
                              <div className="flex-1" data-id="element-1119">
                                <div className="flex justify-between" data-id="element-1120">
                                  <p className="text-sm font-medium text-[#F5F7FA]" data-id="element-1121">
                                    🚨 {item.description}
                                  </p>
                                  <span className="text-xs text-red-500 font-medium" data-id="element-1122">
                                    HIGH PRIORITY
                                  </span>
                                </div>
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mt-1" data-id="element-1123">
                                  Owner: {item.owner}
                                </p>
                              </div>
                            </div>)}
                        {brief.transcriptInsights.commitments.filter(c => c.status === 'pending').map((item, idx) => <div key={idx} className="flex items-start bg-amber-500 bg-opacity-10 p-3 rounded-lg" data-id="element-1124">
                              <AlertTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-1125" />
                              <div className="flex-1" data-id="element-1126">
                                <p className="text-sm font-medium text-[#F5F7FA]" data-id="element-1127">
                                  ⚠️ {item.description}
                                </p>
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mt-1" data-id="element-1128">
                                  Owner: {item.owner}
                                </p>
                              </div>
                            </div>)}
                        {brief.transcriptInsights.commitments.filter(c => c.status === 'resolved').map((item, idx) => <div key={idx} className="flex items-start bg-green-500 bg-opacity-10 p-3 rounded-lg" data-id="element-1129">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-1130" />
                              <div className="flex-1" data-id="element-1131">
                                <p className="text-sm font-medium text-[#F5F7FA]" data-id="element-1132">
                                  ✅ {item.description}
                                </p>
                                <p className="text-xs text-[#F5F7FA] text-opacity-70 mt-1" data-id="element-1133">
                                  Owner: {item.owner}
                                </p>
                              </div>
                            </div>)}
                      </div>
                    </div>
                    <div data-id="element-1134">
                      <h6 className="text-sm font-medium mb-3" data-id="element-1135">
                        Sentiment Highlights
                      </h6>
                      <div className="bg-[#1A2331] p-3 rounded-lg" data-id="element-1136">
                        <ul className="space-y-2" data-id="element-1137">
                          {brief.transcriptInsights.sentimentHighlights.map((highlight, idx) => <li key={idx} className="flex items-start text-sm" data-id="element-1138">
                                <span className="text-[#C8F169] mr-2" data-id="element-1139">•</span>
                                <span data-id="element-1140">{highlight}</span>
                              </li>)}
                        </ul>
                      </div>
                    </div>
                  </div>}
              </div>
              {/* Waitlist CTA */}
              <div className="bg-[#C8F169] bg-opacity-5 border border-[#C8F169] border-opacity-20 rounded-lg p-4" data-id="element-1141">
                <h4 className="font-medium text-[#C8F169] mb-2" data-id="element-1142">
                  You're on the waitlist!
                </h4>
                <p className="text-sm text-[#F5F7FA]" data-id="element-1143">
                  Thanks for your interest! We'll notify you when Prepz.io
                  launches. This is just a preview of what our AI-powered
                  meeting briefs will look like.
                </p>
              </div>
              <Button onClick={onClose} className="w-full" data-id="element-1144">
                Close Preview
              </Button>
            </div>}
        </div>
      </div>
    </div>;
}